import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  styled,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Restaurant,
  Receipt,
  AttachMoney,
  Schedule,
  CheckCircle,
  Cancel,
  Refresh,
  BarChart,
  PieChart,
  ShowChart,
  Search,
  Download,
  Print,
  People,
  LocalDining,
  RoomService,
  DeliveryDining,
  Fastfood,
  AccessTime,
  TrendingDown,
  Percent,
  Star,
  Person,
  EmojiEvents,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
 
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  Scatter,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../../components/sidebars/sidebar_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Styled Components
const StatCard = styled(Card)(({ theme, color = 'primary' }) => ({
  background: `linear-gradient(135deg, ${theme.palette[color].light}20 0%, ${theme.palette[color].main}10 100%)`,
  border: `1px solid ${theme.palette[color].light}30`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  height: 'auto',
  minHeight: '400px',
  background: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minWidth: 120,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
}));

const MetricBadge = styled(Box)(({ theme, type }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 12px',
  borderRadius: '20px',
  backgroundColor: type === 'increase' ? theme.palette.success.light : 
                   type === 'decrease' ? theme.palette.error.light : 
                   theme.palette.grey[200],
  color: type === 'increase' ? theme.palette.success.dark :
         type === 'decrease' ? theme.palette.error.dark :
         theme.palette.text.secondary,
  fontSize: '12px',
  fontWeight: 500,
}));

const CashierAnalyticsDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(max-width:1024px)');
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('total_revenue');
  const [orderDirection, setOrderDirection] = useState('desc');
  
  const [analytics, setAnalytics] = useState(null);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  // CashierAnalyticsDashboard.jsx - Updated API calls

const loadAnalytics = async () => {
  setLoading(true);
  setError('');
  
  try {
    const hotelCode = getHotelCode();
    
    if (!hotelCode) {
      throw new Error('Hotel code not found');
    }

    // Load all transactions (no staff filter for cashier view)
    const params = new URLSearchParams({
      hotel_code: hotelCode,
      period: period,
    });

    // FIXED: Use statistics.php (which exists and is working)
    const response = await fetch(`${API_BASE_URL}/hotel/F&B/statistics.php?${params}`);
    const data = await response.json();

    if (data.success) {
      setAnalytics(data.statistics);
      console.log('Analytics data loaded:', data.statistics);
      
      // Load staff performance data separately
      await loadStaffPerformance(hotelCode, period);
    } else {
      throw new Error(data.error || 'Failed to load analytics');
    }
  } catch (err) {
    console.error('Error loading analytics:', err);
    setError(err.message);
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};

const loadStaffPerformance = async (hotelCode, period) => {
  try {
    // FIXED: Use get_staff.php instead of staff.php
    const staffResponse = await fetch(`${API_BASE_URL}/hotel/F&B/get_staff.php?hotel_code=${hotelCode}`);
    const staffData = await staffResponse.json();
    
    if (staffData.success && staffData.staff) {
      const staffStats = [];
      
      // Get statistics for each staff member using statistics.php with staff_id
      for (const staff of staffData.staff) {
        const statsParams = new URLSearchParams({
          hotel_code: hotelCode,
          period: period,
          staff_id: staff.staff_id || staff.id
        });
        
        // FIXED: Use statistics.php (which supports staff_id parameter)
        const statsResponse = await fetch(`${API_BASE_URL}/hotel/F&B/statistics.php?${statsParams}`);
        const statsData = await statsResponse.json();
        
        if (statsData.success && statsData.statistics) {
          staffStats.push({
            id: staff.id,
            staff_id: staff.staff_id,
            name: `${staff.first_name} ${staff.other_names}`,
            department: staff.department,
            total_orders: statsData.statistics.total_orders || 0,
            total_revenue: statsData.statistics.total_revenue || 0,
            completed_orders: statsData.statistics.completed_orders || 0,
            pending_orders: statsData.statistics.pending_orders || 0,
            cancelled_orders: statsData.statistics.cancelled_orders || 0,
            average_order_value: statsData.statistics.average_order_value || 0,
            served_count: statsData.statistics.served_count || 0,
            served_revenue: statsData.statistics.served_revenue || 0,
            current_staff_sales: statsData.statistics.current_staff_sales || 0,
            current_staff_remittable: statsData.statistics.current_staff_remittable || 0,
          });
        }
      }
      
      setStaffPerformance(staffStats);
      console.log('Staff performance loaded:', staffStats);
    }
  } catch (err) {
    console.error('Error loading staff performance:', err);
  }
};

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortStaffData = (data) => {
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (orderDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      // Prepare data for export
      const exportData = {
        'Summary Statistics': [
          { Metric: 'Total Orders', Value: analytics?.total_orders || 0 },
          { Metric: 'Total Revenue', Value: `₦${(analytics?.total_revenue || 0).toFixed(2)}` },
          { Metric: 'Average Order Value', Value: `₦${(analytics?.average_order_value || 0).toFixed(2)}` },
          { Metric: 'Completed Orders', Value: analytics?.completed_orders || 0 },
          { Metric: 'Pending Orders', Value: analytics?.pending_orders || 0 },
          { Metric: 'Cancelled Orders', Value: analytics?.cancelled_orders || 0 },
          { Metric: 'Low Stock Items', Value: analytics?.low_stock_items || 0 },
        ],
        'Staff Performance': staffPerformance.map(staff => ({
          'Staff Name': staff.name,
          'Department': staff.department,
          'Total Orders': staff.total_orders,
          'Total Revenue': staff.total_revenue,
          'Completed Orders': staff.completed_orders,
          'Pending Orders': staff.pending_orders,
          'Cancelled Orders': staff.cancelled_orders,
          'Average Order Value': staff.average_order_value,
          'Today\'s Sales': staff.current_staff_sales,
          'Remittable Amount': staff.current_staff_remittable,
        })),
        'Popular Items': (analytics?.popular_items || []).map(item => ({
          'Item Name': item.name,
          'Quantity Sold': item.total_quantity,
          'Revenue': item.total_revenue,
          'Order Count': item.order_count,
        })),
        'Order Type Distribution': (analytics?.order_type_distribution || []).map(type => ({
          'Order Type': type.order_type,
          'Count': type.count,
          'Revenue': type.revenue,
        })),
      };

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      Object.keys(exportData).forEach(sheetName => {
        const ws = XLSX.utils.json_to_sheet(exportData[sheetName]);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      
      XLSX.writeFile(wb, `F&B_Analytics_${period}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = () => {
    setExporting(true);
    try {
      const doc = new jsPDF();
      const date = new Date().toLocaleDateString();
      
      doc.setFontSize(20);
      doc.text('F&B Analytics Report', 14, 20);
      doc.setFontSize(12);
      doc.text(`Period: ${period} | Generated: ${date}`, 14, 30);
      
      // Summary section
      doc.setFontSize(14);
      doc.text('Summary Statistics', 14, 45);
      doc.autoTable({
        startY: 50,
        head: [['Metric', 'Value']],
        body: [
          ['Total Orders', analytics?.total_orders || 0],
          ['Total Revenue', `₦${(analytics?.total_revenue || 0).toFixed(2)}`],
          ['Average Order Value', `₦${(analytics?.average_order_value || 0).toFixed(2)}`],
          ['Completed Orders', analytics?.completed_orders || 0],
          ['Pending Orders', analytics?.pending_orders || 0],
          ['Cancelled Orders', analytics?.cancelled_orders || 0],
        ],
        theme: 'striped',
        headStyles: { fillColor: [33, 150, 243] },
      });
      
      // Staff performance table
      const staffBody = staffPerformance.map(staff => [
        staff.name,
        staff.total_orders,
        `₦${staff.total_revenue.toFixed(2)}`,
        staff.completed_orders,
        `₦${staff.average_order_value.toFixed(2)}`,
      ]);
      
      doc.addPage();
      doc.text('Staff Performance', 14, 20);
      doc.autoTable({
        startY: 25,
        head: [['Staff Name', 'Total Orders', 'Total Revenue', 'Completed', 'Avg Order']],
        body: staffBody,
        theme: 'striped',
        headStyles: { fillColor: [33, 150, 243] },
      });
      
      doc.save(`F&B_Analytics_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Chart data preparation from real database data
  const prepareHourlyData = () => {
    if (!analytics?.hourly_trends) return [];
    
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      hourLabel: `${i.toString().padStart(2, '0')}:00`,
      orders: 0,
      revenue: 0
    }));

    analytics.hourly_trends.forEach(item => {
      const hour = parseInt(item.hour);
      if (hour >= 0 && hour < 24) {
        hours[hour].orders = parseInt(item.order_count) || 0;
        hours[hour].revenue = parseFloat(item.revenue) || 0;
      }
    });

    return hours;
  };

  const prepareDailyData = () => {
    if (!analytics?.daily_trends) return [];
    
    return analytics.daily_trends.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: item.date,
      orders: parseInt(item.order_count) || 0,
      revenue: parseFloat(item.revenue) || 0
    }));
  };

  const prepareOrderTypeData = () => {
    if (!analytics?.order_type_distribution) return [];
    
    const orderTypeLabels = {
      'dine_in': 'Dine In',
      'room_service': 'Room Service',
      'takeaway': 'Takeaway',
      'delivery': 'Delivery',
      'Lounge': 'Lounge'
    };

    const orderTypeIcons = {
      'dine_in': <LocalDining />,
      'room_service': <RoomService />,
      'takeaway': <Fastfood />,
      'delivery': <DeliveryDining />,
      'Lounge': <People />
    };

    return analytics.order_type_distribution.map(item => ({
      name: orderTypeLabels[item.order_type] || item.order_type,
      value: parseInt(item.count) || 0,
      revenue: parseFloat(item.revenue) || 0,
      icon: orderTypeIcons[item.order_type] || <Restaurant />
    }));
  };

  const prepareCategoryData = () => {
    if (!analytics?.category_performance) return [];
    
    return analytics.category_performance.map(item => ({
      name: item.category_name,
      revenue: parseFloat(item.total_revenue) || 0,
      quantity: parseInt(item.total_quantity) || 0
    }));
  };

  const preparePopularItemsData = () => {
    if (!analytics?.popular_items) return [];
    
    return analytics.popular_items.map((item, index) => ({
      rank: index + 1,
      name: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
      fullName: item.name,
      sales: parseInt(item.total_quantity) || 0,
      revenue: parseFloat(item.total_revenue) || 0,
      orderCount: parseInt(item.order_count) || 0
    }));
  };

  // Chart colors
  const CHART_COLORS = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const PIE_COLORS = [
    CHART_COLORS.primary,
    CHART_COLORS.secondary,
    CHART_COLORS.success,
    CHART_COLORS.warning,
    CHART_COLORS.error,
    CHART_COLORS.info,
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
  ];

  const tabLabels = [
    { label: 'Dashboard', icon: <TrendingUp /> },
    { label: 'Staff Performance', icon: <People /> },
    { label: 'Item Analytics', icon: <Restaurant /> },
    { label: 'Revenue Analytics', icon: <AttachMoney /> },
  ];

  const renderDashboardTab = () => {
    const hourlyData = prepareHourlyData();
    const dailyData = prepareDailyData();
    const orderTypeData = prepareOrderTypeData();
    const popularItemsData = preparePopularItemsData().slice(0, 5);

    // Calculate growth percentages (mock data - in production would compare with previous period)
    const revenueGrowth = analytics?.total_revenue > 1000 ? 12.5 : -3.2;
    const orderGrowth = analytics?.total_orders > 50 ? 8.3 : -1.5;

    return (
      <Box>
        {/* Key Metrics Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: 'Today Revenue from all Orders',
              value: `₦${(analytics?.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              icon: <AttachMoney sx={{ fontSize: 32 }} />,
              color: 'success',
              subtitle: `${revenueGrowth > 0 ? '+' : ''}${revenueGrowth}% vs last period`,
              trend: revenueGrowth > 0 ? 'up' : 'down',
            },
            {
              title: 'Total Orders',
              value: analytics?.total_orders?.toLocaleString() || '0',
              icon: <Receipt sx={{ fontSize: 32 }} />,
              color: 'primary',
              subtitle: `${orderGrowth > 0 ? '+' : ''}${orderGrowth}% vs last period`,
              trend: orderGrowth > 0 ? 'up' : 'down',
            },
            {
              title: 'Avg Order Value',
              value: `₦${(analytics?.average_order_value || 0).toFixed(2)}`,
              icon: <TrendingUp sx={{ fontSize: 32 }} />,
              color: 'info',
              subtitle: 'Per transaction',
            },
            {
              title: 'Active Staff',
              value: staffPerformance.filter(s => s.total_orders > 0).length,
              icon: <People sx={{ fontSize: 32 }} />,
              color: 'warning',
              subtitle: `${staffPerformance.length} total staff`,
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard color={stat.color}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                        {stat.value}
                      </Typography>
                      {stat.subtitle && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                          {stat.trend === 'up' ? (
                            <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />
                          ) : stat.trend === 'down' ? (
                            <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />
                          ) : null}
                          <Typography variant="caption" color="textSecondary">
                            {stat.subtitle}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: `${stat.color}.light`,
                        borderRadius: '12px',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {React.cloneElement(stat.icon, {
                        sx: { color: `${stat.color}.main` }
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          ))}
        </Grid>

        {/* Main Charts */}
        <Grid container spacing={3}>
          {/* Revenue Trend Chart */}
          <Grid item xs={12} lg={8}>
            <ChartContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  <ShowChart sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Revenue & Orders Trend
                </Typography>
                <Chip label={period === 'today' ? 'Today\'s Hourly Data' : period === 'week' ? 'Last 7 Days' : 'Last 30 Days'} size="small" />
              </Box>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={period === 'today' ? hourlyData : dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey={period === 'today' ? 'hourLabel' : 'date'} 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    interval={period === 'today' ? 3 : 0}
                  />
                  <YAxis 
                    yAxisId="left"
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    tickFormatter={(value) => `₦${value.toLocaleString()}`}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? `₦${Number(value).toLocaleString()}` : value,
                      name === 'revenue' ? 'Revenue' : 'Orders'
                    ]}
                  />
                  <Legend />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="revenue" 
                    name="Revenue"
                    fill={CHART_COLORS.primary}
                    stroke={CHART_COLORS.primary}
                    fillOpacity={0.1}
                  />
                  <Bar 
                    yAxisId="right"
                    dataKey="orders" 
                    name="Orders"
                    fill={CHART_COLORS.secondary}
                    radius={[4, 4, 0, 0]}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Order Type Distribution */}
          <Grid item xs={12} lg={4}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                <PieChart sx={{ verticalAlign: 'middle', mr: 1 }} />
                Order Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPieChart>
                  <Pie
                    data={orderTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
                </RechartsPieChart>
              </ResponsiveContainer>
              
              {/* Order type stats summary */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {orderTypeData.map((type, index) => (
                  <Chip
                    key={index}
                    label={`${type.name}: ${type.value} orders`}
                    size="small"
                    sx={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] + '20', color: PIE_COLORS[index % PIE_COLORS.length] }}
                  />
                ))}
              </Box>
            </ChartContainer>
          </Grid>

          {/* Popular Items */}
          <Grid item xs={12} lg={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                <Restaurant sx={{ verticalAlign: 'middle', mr: 1 }} />
                Top Selling Items
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsBarChart data={popularItemsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    type="number" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                  />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke={theme.palette.text.secondary}
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'sales' ? `${value} units` : `₦${Number(value).toLocaleString()}`,
                      name === 'sales' ? 'Quantity Sold' : 'Revenue'
                    ]}
                  />
                  <Legend />
                  <Bar 
                    dataKey="sales" 
                    name="Quantity Sold"
                    fill={CHART_COLORS.primary}
                    radius={[0, 4, 4, 0]}
                  />
                </RechartsBarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Quick Stats Cards */}
          <Grid item xs={12} lg={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                <Schedule sx={{ verticalAlign: 'middle', mr: 1 }} />
                Order Status Overview
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="success.dark">
                      {analytics?.completed_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="success.dark">
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="warning.dark">
                      {analytics?.pending_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="warning.dark">
                      Pending
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="error.dark">
                      {analytics?.cancelled_orders || 0}
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      Cancelled
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Low Stock Alert */}
              {analytics?.low_stock_items > 0 && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ⚠️ Low Stock Alert
                  </Typography>
                  <Typography variant="body2">
                    {analytics.low_stock_items} item(s) are running low on stock and need attention.
                  </Typography>
                </Alert>
              )}
            </ChartContainer>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderStaffPerformanceTab = () => {
    const sortedStaff = sortStaffData(staffPerformance);
    
    // Calculate top performer
    const topPerformer = staffPerformance.reduce((best, current) => 
      current.total_revenue > best.total_revenue ? current : best, 
      staffPerformance[0] || { total_revenue: 0 }
    );

    // Calculate totals
    const totalStaffRevenue = staffPerformance.reduce((sum, s) => sum + s.total_revenue, 0);
    const totalStaffOrders = staffPerformance.reduce((sum, s) => sum + s.total_orders, 0);
    const averageStaffRevenue = totalStaffRevenue / (staffPerformance.length || 1);

    return (
      <Box>
        {/* Staff Summary Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatCard color="primary">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    <People />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Total Staff</Typography>
                    <Typography variant="h4" fontWeight="bold">{staffPerformance.length}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {staffPerformance.filter(s => s.total_orders > 0).length} active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StatCard color="success">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Total Staff Revenue</Typography>
                    <Typography variant="h4" fontWeight="bold">₦{totalStaffRevenue.toLocaleString()}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg: ₦{averageStaffRevenue.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StatCard color="warning">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                    <EmojiEvents />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Top Performer</Typography>
                    <Typography variant="h6" fontWeight="bold" noWrap>{topPerformer?.name || 'N/A'}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      ₦{(topPerformer?.total_revenue || 0).toLocaleString()} revenue
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>

        {/* Staff Performance Chart */}
        <ChartContainer sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            <BarChart sx={{ verticalAlign: 'middle', mr: 1 }} />
            Staff Revenue Comparison
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <RechartsBarChart data={staffPerformance} margin={{ top: 20, right: 30, left: 40, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="name" 
                stroke={theme.palette.text.secondary}
                fontSize={12}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                fontSize={12}
                tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`₦${Number(value).toLocaleString()}`, 'Revenue']}
              />
              <Legend />
              <Bar 
                dataKey="total_revenue" 
                name="Total Revenue"
                fill={CHART_COLORS.primary}
                radius={[4, 4, 0, 0]}
              >
                {staffPerformance.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.total_revenue === topPerformer?.total_revenue ? CHART_COLORS.warning : CHART_COLORS.primary}
                  />
                ))}
              </Bar>
            </RechartsBarChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Staff Performance Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Staff Performance Details
            </Typography>
            <Chip label={`${staffPerformance.length} Staff Members`} size="small" sx={{ bgcolor: 'white', color: 'primary.main' }} />
          </Box>
          
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('name')}
                    >
                      Staff Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'total_orders'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('total_orders')}
                    >
                      Orders
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'total_revenue'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('total_revenue')}
                    >
                      Revenue
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'completed_orders'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('completed_orders')}
                    >
                      Completed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'average_order_value'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('average_order_value')}
                    >
                      Avg Order
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'cancelled_orders'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('cancelled_orders')}
                    >
                      Cancelled
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'current_staff_sales'}
                      direction={orderDirection}
                      onClick={() => handleRequestSort('current_staff_sales')}
                    >
                      Today's Sales
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedStaff.map((staff, index) => {
                  const isTopPerformer = staff.total_revenue === topPerformer?.total_revenue && topPerformer?.total_revenue > 0;
                  return (
                    <TableRow key={staff.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: isTopPerformer ? 'warning.main' : 'primary.main' }}>
                            {isTopPerformer ? <EmojiEvents sx={{ fontSize: 16 }} /> : staff.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={isTopPerformer ? 'bold' : 'normal'}>
                              {staff.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {staff.department}
                            </Typography>
                          </Box>
                          {isTopPerformer && (
                            <Chip label="Top Performer" size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{staff.total_orders}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          ₦{staff.total_revenue.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Typography variant="body2">{staff.completed_orders}</Typography>
                          <Chip 
                            label={`${((staff.completed_orders / (staff.total_orders || 1)) * 100).toFixed(0)}%`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">₦{staff.average_order_value.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main">
                          {staff.cancelled_orders}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="info.main">
                          ₦{staff.current_staff_sales.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {staffPerformance.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                        No staff performance data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  const renderItemAnalyticsTab = () => {
    const popularItemsData = preparePopularItemsData();
    const categoryData = prepareCategoryData();

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Top Items Table */}
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" fontWeight="bold">
                  <Restaurant sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Menu Item Performance
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Item Name</TableCell>
                      <TableCell align="right">Quantity Sold</TableCell>
                      <TableCell align="right">Total Revenue</TableCell>
                      <TableCell align="right">Order Count</TableCell>
                      <TableCell align="right">Avg Price</TableCell>
                      <TableCell>Popularity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {popularItemsData.map((item) => {
                      const maxSales = popularItemsData[0]?.sales || 1;
                      const popularityPercent = (item.sales / maxSales) * 100;
                      
                      return (
                        <TableRow key={item.rank}>
                          <TableCell>
                            {item.rank === 1 ? (
                              <Chip label="#1" size="small" color="warning" />
                            ) : item.rank === 2 ? (
                              <Chip label="#2" size="small" color="default" />
                            ) : item.rank === 3 ? (
                              <Chip label="#3" size="small" color="default" />
                            ) : (
                              <Typography variant="body2">#{item.rank}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={item.fullName}>
                              <Typography variant="body2">{item.name}</Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {item.sales} units
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main">
                              ₦{item.revenue.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{item.orderCount} orders</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              ₦{(item.revenue / item.sales).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={popularityPercent} 
                                sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                color={popularityPercent > 70 ? 'success' : popularityPercent > 40 ? 'warning' : 'error'}
                              />
                              <Typography variant="caption" color="textSecondary">
                                {popularityPercent.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {popularItemsData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                            No item sales data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderRevenueAnalyticsTab = () => {
    const dailyData = prepareDailyData();
    const categoryData = prepareCategoryData();

    // Calculate revenue insights
    const totalRevenue = analytics?.total_revenue || 0;
    const avgDailyRevenue = dailyData.length > 0 ? totalRevenue / dailyData.length : totalRevenue;
    const peakRevenueDay = dailyData.reduce((max, day) => day.revenue > max.revenue ? day : max, dailyData[0] || { revenue: 0 });
    
    // Order type revenue breakdown
    const orderTypeRevenue = prepareOrderTypeData();

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Revenue Summary Cards */}
          <Grid item xs={12} md={4}>
            <StatCard color="success">
              <CardContent>
                <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                <Typography variant="h3" fontWeight="bold" color="success.main">
                  ₦{totalRevenue.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Across {analytics?.total_orders || 0} orders
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StatCard color="info">
              <CardContent>
                <Typography variant="body2" color="textSecondary">Average Daily Revenue</Typography>
                <Typography variant="h3" fontWeight="bold" color="info.main">
                  ₦{avgDailyRevenue.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Based on {dailyData.length} days
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          
         

         

          {/* Revenue by Order Type */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                <PieChart sx={{ verticalAlign: 'middle', mr: 1 }} />
                Revenue by Order Type
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={orderTypeRevenue}
                    cx="50%"
                    cy="50%"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    dataKey="revenue"
                  >
                    {orderTypeRevenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`₦${Number(value).toLocaleString()}`, 'Revenue']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Grid>

          {/* Revenue Insights */}
          <Grid item xs={12} md={6}>
            <ChartContainer>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                <Percent sx={{ verticalAlign: 'middle', mr: 1 }} />
                Revenue Insights
              </Typography>
              <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Top Revenue Source
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {orderTypeRevenue[0]?.name || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    ₦{orderTypeRevenue[0]?.revenue?.toLocaleString() || 0}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Revenue Breakdown
                  </Typography>
                  {orderTypeRevenue.map((type, index) => (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{type.name}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ₦{type.revenue.toLocaleString()}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(type.revenue / totalRevenue) * 100}
                        sx={{ height: 6, borderRadius: 3 }}
                        color="primary"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </ChartContainer>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderDashboardTab();
      case 1:
        return renderStaffPerformanceTab();
      case 2:
        return renderItemAnalyticsTab();
      case 3:
        return renderRevenueAnalyticsTab();
      default:
        return renderDashboardTab();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={handleRefresh} sx={{ ml: 2 }} size="small">
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1600px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                📊 Cashier Analytics Dashboard
              </Typography>
              <Typography variant="subtitle1">
                Complete overview of all Food & Beverage transactions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export to Excel">
                <Button 
                  variant="contained" 
                  startIcon={<Download />}
                  onClick={exportToExcel}
                  disabled={exporting}
                  sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
                >
                  Excel
                </Button>
              </Tooltip>
             
            </Box>
          </Box>
        </Paper>

        {/* Controls Section */}
        <Paper elevation={1} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search analytics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Time Period</InputLabel>
                  <Select value={period} onChange={handlePeriodChange} label="Time Period">
                    <MenuItem value="today">Today (Hourly)</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs Section */}
        <Paper elevation={1} sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
          <StyledTabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {tabLabels.map((tab, index) => (
              <StyledTab 
                key={index} 
                label={tab.label}
                icon={tab.icon} 
                iconPosition="start" 
              />
            ))}
          </StyledTabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CashierAnalyticsDashboard;